import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import ProductionTable from './productionTable';
import DryerStatusPanel, { dryerStageLabel, computeElapsedMs, formatElapsedMs } from './DryerStatusPanel';
import { useFilling } from '../context/FillingContext';
import useCylinderSummary from '../hooks/useCylinderSummary';
import {
  saveActiveFilling, loadActiveFilling, clearActiveFilling,
  MAX_HELD_FILLINGS, loadHeldFillings, holdFilling, heldFillingCount, resumeHeldFilling,
} from '../utils/fillingQueue';
import { loadRecentClients, recordRecentClient } from '../utils/recentClients';

import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from 'chart.js';
import { dayKeyDR, dayLabel, formatDay, formatDateTime } from '../utils/formatDate';
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

// datetime-local inputs need local (not UTC) wall-clock values — matches
// setCurrentTime's existing "Ahora" button conversion below.
const toLocalInputValue = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

// Only Caribbean Gas Company / IntMed's own cylinders are CC — everything
// else is PC. Matches the same rule in manifold-pwa's ContributionTab.jsx
// (typeForClient), so the type never needs to be picked by hand.
const COMPANY_CLIENT = 'Caribbean Gas Company / IntMed';
const typeForClient = (client) => (client?.trim().toLowerCase() === COMPANY_CLIENT.toLowerCase() ? 'CC' : 'PC');

const getFillingLabels = (entries) => {
  const dates = entries.map(e => dayKeyDR(e.startTime));
  return [...new Set(dates)].sort();
};

const getFillerDatasets = (entries) => {
  const byFiller = {};

  entries.forEach(e => {
    const filler = e.userName || 'Sin Nombre';
    const date = dayKeyDR(e.startTime);
    const total = e.cylinders?.reduce((sum, c) => sum + (c.amount || 0), 0);

    if (!byFiller[filler]) byFiller[filler] = {};
    byFiller[filler][date] = (byFiller[filler][date] || 0) + total;
  });

  const allDates = getFillingLabels(entries);
  return Object.keys(byFiller).map(filler => ({
    label: filler,
    data: allDates.map(date => byFiller[filler][date] || 0),
    borderWidth: 2,
    fill: false,
    tension: 0.4,
  }));
};



const CylindersFilling = ({
  refreshTable,
  addLogEntry,
  productionStatus,
  setProductionStatus,
  userName,
  notes
}) => {
  const isOperacionRunning = productionStatus.operacion.isRunning;
  const activeGas = productionStatus.activeGas; // 'o2' | 'n2' | null
  const { entries, setEntries, refreshFillings } = useFilling();
  const [clientList, setClientList] = useState([]);
  const [forms, setForms] = useState(() => {
    const saved = loadActiveFilling(userName);
    return saved?.forms?.length ? saved.forms : [{ amount: 0, client: COMPANY_CLIENT }];
  });
  const [startTime, setStartTime] = useState(() => {
    const saved = loadActiveFilling(userName);
    return saved?.startedAt ? toLocalInputValue(new Date(saved.startedAt)) : '';
  });
  const [endTime, setEndTime] = useState('');
  const [now, setNow] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showDropdownIndex, setShowDropdownIndex] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(null); // null | 'operacion' | 'o2' | 'n2' — which stream's stop-reason modal is open
  const [stopReason, setStopReason] = useState('');
  const [stopNotes, setStopNotes] = useState('');
  const [dryerConfirmState, setDryerConfirmState] = useState(null);
  const [checkingDryers, setCheckingDryers] = useState(false);
  const [dryerEditSignal, setDryerEditSignal] = useState(0);
  const [dryerReloadSignal, setDryerReloadSignal] = useState(0);

  // Automatic filling-clock tracking (Iniciar/Pausar/Retener/Finalizar
  // Llenado) layered on top of the existing manual Hora de Inicio/Fin
  // fields — it drives the live timer and pause/hold math, while Guardar
  // stays the actual persist step so backdating by hand still works exactly
  // as before. Hydrated from localStorage so a page reload (or the app
  // being left open across a shift change) doesn't lose an in-progress
  // filling's clock.
  const [fillingStartedAt, setFillingStartedAt] = useState(() => {
    const saved = loadActiveFilling(userName);
    return saved?.startedAt ? new Date(saved.startedAt) : null;
  });
  const [pausedAt, setPausedAt] = useState(() => {
    const saved = loadActiveFilling(userName);
    return saved?.pausedAt ? new Date(saved.pausedAt) : null;
  });
  const [pausedDurationMs, setPausedDurationMs] = useState(() => {
    const saved = loadActiveFilling(userName);
    return saved?.pausedDurationMs || 0;
  });
  const [heldCount, setHeldCount] = useState(() => heldFillingCount(userName));
  const [heldItems, setHeldItems] = useState([]);
  const [heldModalOpen, setHeldModalOpen] = useState(false);
  const [heldCapModalOpen, setHeldCapModalOpen] = useState(false);
  const [recentClients, setRecentClients] = useState(() => loadRecentClients(userName));
  const [longFillingWarning, setLongFillingWarning] = useState(null); // {startTimeDate, endTimeDate, pausedMs} | null
  const [confirmingLong, setConfirmingLong] = useState(false);

  const [showDamageModal, setShowDamageModal] = useState(false);
const [damageQty, setDamageQty] = useState('');
const [damageOwner, setDamageOwner] = useState('');
const [damageNotes, setDamageNotes] = useState('');
const [showDamageSuccess, setShowDamageSuccess] = useState(false);
const [showFillingOrderModal, setShowFillingOrderModal] = useState(false);
const [fillingOrderData, setFillingOrderData] = useState([]);
const [savingRowId, setSavingRowId] = useState(null); // tracks which row is saving
const [showOrderChangedAlert, setShowOrderChangedAlert] = useState(false); // admin changed the order — needs acknowledgement



// Marks a client as done and drops it from the visible list — per the
// FillingOrder queue, only clients still pending should stay on screen.
const markCompleted = async (id) => {
  try {
    setSavingRowId(id);
    const res = await fetch(`/api/filling-order/${id}/completed`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: true }),
    });

    if (!res.ok) throw new Error('Failed to save completion');

    setFillingOrderData((prev) => prev.filter((entry) => entry._id !== id));
  } catch (err) {
    console.error('❌ Error marking as completed:', err);
  } finally {
    setSavingRowId(null);
  }
};





  useEffect(() => {
    fetch('/api/clients')
      .then(res => res.json())
      .then(data => setClientList(data))
      .catch(err => console.error('Error al obtener clientes:', err));
  }, []);

  // Drives the large "current filling" timer — only ticks while a filling
  // is actively being tracked (after Iniciar Llenado), matching the dryer
  // status panel's same pattern.
  useEffect(() => {
    if (!fillingStartedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [fillingStartedAt]);

  // Mirrors the in-progress filling to localStorage on every relevant
  // change so it survives a page reload.
  useEffect(() => {
    if (fillingStartedAt) {
      saveActiveFilling(userName, {
        startedAt: fillingStartedAt.toISOString(),
        pausedAt: pausedAt ? pausedAt.toISOString() : null,
        pausedDurationMs,
        forms,
      });
    } else {
      clearActiveFilling(userName);
    }
  }, [userName, fillingStartedAt, pausedAt, pausedDurationMs, forms]);

  useEffect(() => {
    if (!heldModalOpen) return;
    setHeldItems(loadHeldFillings(userName));
  }, [heldModalOpen, userName]);

 // Polls whether the admin has changed the filling order since we last
 // acknowledged it, independent of whether the order modal is even open.
 useEffect(() => {
  const checkOrderVersion = async () => {
    try {
      const res = await fetch('/api/filling-order/meta');
      const meta = await res.json();
      const ackedVersion = localStorage.getItem('fillingOrderAckVersion');

      if (ackedVersion === null) {
        // First time this browser has ever checked — nothing to acknowledge yet.
        localStorage.setItem('fillingOrderAckVersion', String(meta.version ?? 0));
        return;
      }
      if (Number(ackedVersion) !== (meta.version ?? 0)) {
        setShowOrderChangedAlert(true);
      }
    } catch (err) {
      console.error('❌ Error al verificar la versión del orden de llenado:', err);
    }
    fetchFillingOrder(); // keep the pending-clients badge fresh even while the modal is closed
  };

  checkOrderVersion();
  const interval = setInterval(checkOrderVersion, 30000);
  return () => clearInterval(interval);
}, []);

const acknowledgeOrderChange = async () => {
  try {
    const res = await fetch('/api/filling-order/meta');
    const meta = await res.json();
    localStorage.setItem('fillingOrderAckVersion', String(meta.version ?? 0));
  } catch (err) {
    console.error('❌ Error al confirmar la versión del orden de llenado:', err);
  }
  setShowOrderChangedAlert(false);
  fetchFillingOrder();
  setShowFillingOrderModal(true);
};



 const { received, remaining, pendingToFillByClient, contributions, refreshSummary } = useCylinderSummary();

  // Clients the filler is allowed to pick: the company's own CC cylinders
  // (never backed by a real contribution, see CLAUDE.md) are always
  // available; every other client only shows up once it has tanks
  // contributed-but-not-yet-filled.
  const eligibleClients = [COMPANY_CLIENT, ...Object.keys(pendingToFillByClient).sort()];

  // How much more of `client` can still be entered across the whole form,
  // excluding whatever `excludeIndex`'s own line already claims — lets two
  // lines split one client's balance without either one alone appearing to
  // exceed it. Uncapped (Infinity) for the company's own CC cylinders.
  const remainingForClient = (client, excludeIndex) => {
    if (typeForClient(client) === 'CC') return Infinity;
    const pending = pendingToFillByClient[client] || 0;
    const claimedByOtherLines = forms.reduce(
      (sum, f, i) => (i !== excludeIndex && f.client === client ? sum + (parseInt(f.amount, 10) || 0) : sum),
      0
    );
    return Math.max(0, pending - claimedByOtherLines);
  };


  

  const generateFillingID = () => `CGC-${Math.floor(100000 + Math.random() * 900000)}`;

  const handleAmountChange = (index, increment) => {
    const updatedForms = [...forms];
    const max = remainingForClient(updatedForms[index].client, index);
    updatedForms[index].amount = Math.min(max, Math.max(0, (updatedForms[index].amount || 0) + increment));
    setForms(updatedForms);
  };

  const handleAmountInput = (index, raw) => {
    const updatedForms = [...forms];
    const num = parseInt(raw, 10);
    const max = remainingForClient(updatedForms[index].client, index);
    updatedForms[index].amount = raw === '' || Number.isNaN(num) ? 0 : Math.min(max, Math.max(0, num));
    setForms(updatedForms);
  };


// The FillingOrder queue is now fully admin-curated (added/reordered from the
// admin dashboard) — just show whatever's still pending, in position order.
const fetchFillingOrder = async () => {
  try {
    const res = await fetch('/api/filling-order');
    const data = await res.json();
    const pending = (Array.isArray(data) ? data : [])
      .filter(entry => !entry.completed)
      .sort((a, b) => a.position - b.position);
    setFillingOrderData(pending);
  } catch (err) {
    console.error('❌ Error fetching filling order:', err);
  }
};





  const addForm = () => setForms([...forms, { amount: 0, client: COMPANY_CLIENT, entryId: null }]);
  const deleteForm = (index) => setForms(forms.filter((_, i) => i !== index));

  const handleClientSearch = (index, value) => {
    const updatedForms = [...forms];
    updatedForms[index].client = value;
    updatedForms[index].entryId = null; // typing a different client invalidates whatever pedido was picked
    setForms(updatedForms);
    setShowDropdownIndex(index);
  };

  const selectClient = (index, client) => {
    const updatedForms = [...forms];
    updatedForms[index].client = client;
    updatedForms[index].entryId = null; // a new client's pedido list is different — start unselected
    const max = remainingForClient(client, index);
    updatedForms[index].amount = Math.min(max, updatedForms[index].amount || 0);
    setForms(updatedForms);
    setShowDropdownIndex(null);
  };

  const selectPedido = (index, entryId) => {
    const updatedForms = [...forms];
    updatedForms[index].entryId = entryId || null;
    setForms(updatedForms);
  };

  // This client's own pedido-linked contributions (ventasPedidoId set) — most clients have none, in which case the
  // picker stays hidden and utils/pedidoLlenado.js falls back to its FIFO approximation for that line.
  const pedidosForClient = (client) => contributions.filter(c => c.client === client && c.ventasPedidoId);

  const handleSaveDamageReport = async () => {
  if (!damageQty || !damageOwner) {
    alert("Cantidad y propietario son requeridos.");
    return;
  }

  const report = {
    quantity: Number(damageQty),
    owner: damageOwner,
    notes: damageNotes,
    reportedBy: userName
  };

  try {
    const res = await fetch('/api/damaged-tanks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report)
    });

    if (!res.ok) throw new Error('Error al guardar');

    
    setShowDamageModal(false);
    setShowDamageSuccess(true);

    setDamageQty('');
    setDamageOwner('');
    setDamageNotes('');

    await refreshSummary();

  } catch (err) {
    alert('Error al guardar el reporte.');
    console.error(err);
  }
};

  const setCurrentTime = (setter) => {
    const now = new Date();
    const localISOString = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setter(localISOString);
  };

  // "Iniciar Llenado" requires the operator to have already picked what's
  // actually being filled — starting a clock for nothing was the original
  // gap reported. Mirrors saveForm's own amount/client validation below so
  // a filling that couldn't be saved can't be started either.
  const canStartFilling = forms.length > 0 && forms.every((f) => (Number(f.amount) || 0) > 0 && f.client?.trim());

  const handleStartFilling = () => {
    if (!canStartFilling) return;
    const startedAt = new Date();
    setFillingStartedAt(startedAt);
    setPausedAt(null);
    setPausedDurationMs(0);
    setStartTime(toLocalInputValue(startedAt));
    setEndTime('');
  };

  const handlePauseFilling = () => setPausedAt(new Date());
  const handleResumeFilling = () => {
    setPausedDurationMs((prev) => prev + Math.max(0, Date.now() - pausedAt.getTime()));
    setPausedAt(null);
  };

  // Freezes the clock exactly like a pause (if it wasn't already paused),
  // stores it in the held slot, then clears the form back to blank so a new
  // filling can start — the held one keeps its elapsed time untouched until
  // it's resumed later.
  const handleHoldFilling = () => {
    if (heldCount >= MAX_HELD_FILLINGS) {
      setHeldCapModalOpen(true);
      return;
    }
    const effectivePausedAt = pausedAt || new Date();
    holdFilling(userName, {
      startedAt: fillingStartedAt.toISOString(),
      pausedAt: effectivePausedAt.toISOString(),
      pausedDurationMs,
      forms,
      gas: activeGas,
    });
    setForms([{ amount: 0, client: COMPANY_CLIENT }]);
    setFillingStartedAt(null);
    setPausedAt(null);
    setPausedDurationMs(0);
    setStartTime('');
    setEndTime('');
    setHeldCount(heldFillingCount(userName));
  };

  const handleResumeHeld = (id) => {
    const resumed = resumeHeldFilling(userName, id);
    if (!resumed) return;
    setForms(resumed.forms?.length ? resumed.forms : [{ amount: 0, client: COMPANY_CLIENT }]);
    setFillingStartedAt(new Date(resumed.startedAt));
    setPausedAt(resumed.pausedAt ? new Date(resumed.pausedAt) : null);
    setPausedDurationMs(resumed.pausedDurationMs || 0);
    setStartTime(toLocalInputValue(new Date(resumed.startedAt)));
    setEndTime('');
    setHeldCount(heldFillingCount(userName));
    setHeldModalOpen(false);

    // The gas active when this filling was held may no longer be the gas
    // active now (e.g. someone stopped/switched it from another device
    // while this filler still had it held) — the server derives `gas` from
    // whatever's active *at save time*, so warn now rather than let it
    // silently get attributed to the wrong gas on Guardar.
    if (resumed.gas && resumed.gas !== activeGas) {
      setErrorMessage(
        `Este llenado se retuvo mientras se producía ${resumed.gas.toUpperCase()}, pero el gas activo ahora es ${activeGas ? activeGas.toUpperCase() : 'ninguno'}. Si lo guarda así, se registrará como ${activeGas ? activeGas.toUpperCase() : 'sin gas activo (no se podrá guardar)'}.`
      );
    }
  };

  // A filling this long is unusual enough to be worth a second look before
  // it's saved — most likely a forgotten Pausar/Retener (clock path) or a
  // typo'd manual time (Guardar path), not a real 70+ minute fill.
  const LONG_FILLING_THRESHOLD_MS = 70 * 60 * 1000;

  // Shared save path for both the automatic clock (Finalizar Llenado) and
  // the manual Hora de Inicio/Fin + Guardar flow — same validation, same
  // POST, same reset-on-success. pausedMs is only nonzero for the clock
  // path (manual entry has no pause tracking, so it's a plain diff).
  // `skipLongCheck` is set only by confirmLongFilling, replaying a save the
  // operator already confirmed past the threshold below.
  const persistFilling = async (startTimeDate, endTimeDate, pausedMs, { skipLongCheck = false } = {}) => {
    if (forms.some(f => (Number(f.amount) || 0) <= 0)) {
      setErrorMessage('Todas las cantidades deben ser mayores a 0.');
      return false;
    }
    if (!startTimeDate || !endTimeDate || startTimeDate >= endTimeDate) {
      setErrorMessage('Proporcione horas de inicio y fin válidas.');
      return false;
    }

    const activeMs = (endTimeDate - startTimeDate) - (pausedMs || 0);
    if (!skipLongCheck && activeMs > LONG_FILLING_THRESHOLD_MS) {
      setLongFillingWarning({ startTimeDate, endTimeDate, pausedMs });
      return false;
    }

    const ccCount = forms
      .filter(f => typeForClient(f.client) === 'CC')
      .reduce((sum, f) => sum + (Number(f.amount) || 0), 0);

    const pcCount = forms
      .filter(f => typeForClient(f.client) === 'PC')
      .reduce((sum, f) => sum + (Number(f.amount) || 0), 0);

    const totalCylinders = ccCount + pcCount;

    if (totalCylinders > remaining) {
      setErrorMessage(`Solo hay ${remaining} cilindros disponibles. Intentas guardar ${totalCylinders}.`);
      return false;
    }

    const entry = {
      fillingID: generateFillingID(),
      date: startTimeDate.toISOString(),
      totalCylinders,
      ccCount,
      pcCount,
      clients: forms.map(f => f.client).filter(Boolean).join(', '),
      startTime: startTimeDate,
      endTime: endTimeDate,
      totalTime: Math.floor(((endTimeDate - startTimeDate) - (pausedMs || 0)) / 60000),
      completedBy: userName,
      notes: [],
      cylinders: forms.map(form => ({
        client: form.client,
        type: typeForClient(form.client),
        amount: parseInt(form.amount, 10) || 0,
        entryId: form.entryId || null
      })),
    };

    try {
      const res = await fetch('/api/fillings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!res.ok) {
        if (res.status === 409) {
          // Server says no gas stream is active anymore (stopped from
          // elsewhere, e.g. another device) — clear it locally so the UI
          // reflects that immediately; the next poll reconciles the rest.
          setProductionStatus((prev) => ({ ...prev, o2: { isRunning: false }, n2: { isRunning: false }, activeGas: null }));
          const { error } = await res.json().catch(() => ({}));
          throw new Error(error || 'No hay producción de O2 ni N2 activa.');
        }
        throw new Error('Error al guardar');
      }

      // On success: refresh and reset UI
      await refreshTable?.();
      await refreshFillings?.();
      forms.forEach((f) => recordRecentClient(userName, f.client));
      setRecentClients(loadRecentClients(userName));
      setForms([{ amount: 0, client: COMPANY_CLIENT }]);
      setStartTime('');
      setEndTime('');
      setErrorMessage('');
      setFillingStartedAt(null);
      setPausedAt(null);
      setPausedDurationMs(0);
      clearActiveFilling(userName);

      setShowSuccessModal(true);
      return true;
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || 'No se pudo guardar el llenado. Intente de nuevo.');
      return false;
    }
  };

  // Finalizar Llenado now saves the filling immediately (using the clock's
  // own start time and live pause total) instead of just freezing the
  // clock and waiting for a separate Guardar click — on success the whole
  // card resets to "Iniciar Llenado" since a new filling starts fresh. On
  // failure (e.g. amounts still at 0) the clock keeps running so the
  // operator can fix the form and try again.
  const handleFinishFilling = async () => {
    const end = new Date();
    const totalPausedMs = pausedDurationMs + (pausedAt ? Math.max(0, end.getTime() - pausedAt.getTime()) : 0);
    await persistFilling(fillingStartedAt, end, totalPausedMs);
  };

  // Pure manual entry (Hora de Inicio/Fin fields, no clock involved) — a
  // plain diff, unchanged from before Finalizar Llenado existed.
  const saveForm = () => persistFilling(
    startTime ? new Date(startTime) : null,
    endTime ? new Date(endTime) : null,
    0
  );

  // Replays the exact save that persistFilling deferred once the operator
  // confirms past the long-filling warning.
  const confirmLongFilling = async () => {
    if (!longFillingWarning) return;
    setConfirmingLong(true);
    await persistFilling(
      longFillingWarning.startTimeDate,
      longFillingWarning.endTimeDate,
      longFillingWarning.pausedMs,
      { skipLongCheck: true },
    );
    setConfirmingLong(false);
    setLongFillingWarning(null);
  };


  // Re-reads all three streams from the server after any change — the
  // cascade logic (O2/N2 mutual exclusion, Operación stop force-stopping
  // gas) lives server-side, so the frontend reflects it via a fresh fetch
  // rather than trying to predict it locally.
  const refreshProductionStatus = async () => {
    try {
      const res = await fetch('/api/production-events/current');
      const data = await res.json();
      setProductionStatus(data);
    } catch (err) {
      console.error('❌ Error refreshing production status:', err);
    }
  };

  const logProductionEvent = async (type, stream, reason = '', notes = '') => {
    try {
      await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, stream, operator: userName, reason, notes }),
      });
    } catch (err) {
      console.error('❌ Error logging production event:', err);
    } finally {
      await refreshProductionStatus();
    }
  };

  const doStartProduction = async () => {
    await logProductionEvent('start', 'operacion');
    addLogEntry('Operación iniciada.');
    setDryerConfirmState(null);
    setDryerReloadSignal((s) => s + 1);
  };

  // Dryer stage timers freeze while production is stopped (see
  // DryerStatusPanel), so before actually starting, check whether the
  // dryers are configured at all — if so, the operator needs to confirm the
  // frozen state still matches the physical valve positions before
  // production (and the dryer clocks) resume.
  const toggleProduction = async () => {
    if (isOperacionRunning) {
      setShowStopModal('operacion');
      return;
    }
    setCheckingDryers(true);
    try {
      const res = await fetch('/api/dryer-events/current');
      const data = await res.json();
      if (data.configured) {
        setDryerConfirmState(data);
      } else {
        await doStartProduction();
      }
    } catch (err) {
      console.error('❌ Error al verificar el estado de los secadores:', err);
      await doStartProduction();
    } finally {
      setCheckingDryers(false);
    }
  };

  const confirmDryersAndStartProduction = async () => {
    try {
      await fetch('/api/dryer-events/confirm', { method: 'POST' });
    } catch (err) {
      console.error('❌ Error al confirmar el estado de los secadores:', err);
    }
    await doStartProduction();
  };

  const editDryersInstead = () => {
    setDryerConfirmState(null);
    setDryerEditSignal((s) => s + 1);
  };

  // O2/N2 start has none of Operación's dryer-valve-confirmation ceremony —
  // it's a plain fire-and-refresh action; mutual exclusion is enforced and
  // logged server-side.
  const startGas = (stream) => logProductionEvent('start', stream);

  const STREAM_STOP_LABEL = { operacion: 'Operación', o2: 'Producción de O2', n2: 'Producción de N2' };

  const handleStopProduction = async () => {
    if (!stopReason) return alert('Seleccione un motivo');
    const stream = showStopModal;
    await logProductionEvent('stop', stream, stopReason, stopNotes);
    addLogEntry(`${STREAM_STOP_LABEL[stream]} detenida. Motivo: ${stopReason}`);
    setDryerReloadSignal((s) => s + 1);
    setShowStopModal(null);
    setStopReason('');
    setStopNotes('');
  };

const generateFillerDatasets = (entries) => {
  const dateLabels = [...new Set(entries.map(e => dayKeyDR(e.startTime)))];

  const fillerMap = {};
  entries.forEach(e => {
    const date = dayKeyDR(e.startTime);
    const filler = e.user || 'Desconocido';
    if (!fillerMap[filler]) fillerMap[filler] = {};
    fillerMap[filler][date] = (fillerMap[filler][date] || 0) + (e.totalCylinders || 0);
  });

  return Object.entries(fillerMap).map(([filler, dateCounts], index) => ({
    label: filler,
    data: dateLabels.map(date => dateCounts[date] || 0),
    borderColor: `hsl(${index * 50 % 360}, 70%, 50%)`,
    backgroundColor: `hsl(${index * 50 % 360}, 70%, 90%)`,
    tension: 0.3
  }));
};


const getFillingLabels = (entries) => {
  const dates = entries.map(e => dayKeyDR(e.startTime));
  return [...new Set(dates)].sort();
};

const getFillerDatasets = (entries) => {
  const byFiller = {};

  entries.forEach(e => {
    const filler = e.userName || 'Sin Nombre';
    const date = dayKeyDR(e.startTime);
    const total = e.cylinders?.reduce((sum, c) => sum + (c.amount || 0), 0);

    if (!byFiller[filler]) byFiller[filler] = {};
    byFiller[filler][date] = (byFiller[filler][date] || 0) + total;
  });

  const allDates = getFillingLabels(entries);
  return Object.keys(byFiller).map(filler => ({
    label: filler,
    data: allDates.map(date => byFiller[filler][date] || 0),
    borderWidth: 2,
    fill: false,
    tension: 0.4,
  }));
};




return (
  <div className="container py-4">
    {/* 🔷 Summary Cards */}
    <div className="row mb-4 text-center">
      <div className="col-md-6 mb-3">
        <div className="card bg-info text-white shadow-sm h-100">
          <div className="card-body">
            <h5 className="card-title">Cilindros Recibidos</h5>
            <p className="card-text display-5 fw-bold" style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center' }} >{received}</p>
            
          </div>
        </div>
      </div>
      <div className="col-md-6 mb-3">
        <div
          className="card bg-success text-white shadow-sm h-100"
          onClick={() => { fetchFillingOrder(); setShowFillingOrderModal(true); }}
          style={{ cursor: 'pointer', position: 'relative' }}
        >
          {fillingOrderData.length > 0 && (
            <span
              className="badge bg-warning text-dark"
              style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '0.8rem' }}
            >
              {fillingOrderData.length} en cola
            </span>
          )}
          <div className="card-body">
            <h5 className="card-title">Cilindros Restantes</h5>
            <p className="card-text display-5 fw-bold" style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center' }}>{remaining}</p>
          </div>
        </div>
      </div>
    </div>

    {/* 🟡 Operación Toggle + O2/N2 Production Card. O2/N2 are mutually
        exclusive (server-enforced) and require Operación to be running
        first — the two buttons below stay disabled+hinted until then,
        matching this app's existing "always visible, disabled" convention
        rather than hiding the whole card. */}
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body text-center">
        <button
          className={`btn ${isOperacionRunning ? 'btn-danger' : 'btn-success'} btn-lg px-5`}
          onClick={toggleProduction}
          disabled={checkingDryers}
        >
          <i className={`bi ${isOperacionRunning ? 'bi-stop-circle-fill' : 'bi-play-circle-fill'} me-2`} />
          {checkingDryers ? 'Verificando secadores...' : isOperacionRunning ? 'Detener Operación' : 'Iniciar Operación'}
        </button>

        <div className="d-flex justify-content-center mt-3" style={{ gap: 12 }}>
          <button
            className="btn btn-outline-primary"
            onClick={() => startGas('o2')}
            disabled={!isOperacionRunning || activeGas === 'o2'}
          >
            Iniciar Producción O2
          </button>
          <button
            className="btn btn-outline-info"
            onClick={() => startGas('n2')}
            disabled={!isOperacionRunning || activeGas === 'n2'}
          >
            Iniciar Producción N2
          </button>
        </div>

        {!isOperacionRunning && (
          <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
            Inicie la Operación para poder iniciar producción de O2 o N2.
          </div>
        )}
        {activeGas && (
          <div className="mt-2">
            Produciendo {activeGas.toUpperCase()} —{' '}
            <button
              className="btn btn-link btn-sm p-0"
              onClick={() => setShowStopModal(activeGas)}
              disabled={heldCount > 0}
              title={heldCount > 0 ? 'Finaliza o reanuda los llenados en espera antes de detener este gas.' : undefined}
            >
              Detener
            </button>
            {heldCount > 0 && (
              <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                No puedes cambiar de gas mientras haya llenados en espera. Finaliza o reanuda {heldCount === 1 ? 'el llenado en espera' : 'los llenados en espera'} primero.
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* ⏱ Current Filling Clock — Iniciar/Pausar/Retener/Finalizar Llenado.
        Optional convenience layered on top of the manual Hora de
        Inicio/Fin fields below: Guardar still does the actual save, so
        backdating by hand keeps working exactly as before. */}
    {activeGas && (
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div className="fw-bold">Llenado Actual</div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>Produciendo {activeGas.toUpperCase()}</div>
            </div>
            {heldCount > 0 && (
              <button className="btn btn-warning btn-sm" onClick={() => setHeldModalOpen(true)}>
                {heldCount} en espera
              </button>
            )}
          </div>

          {!fillingStartedAt ? (
            <>
              <button className="btn btn-success" onClick={handleStartFilling} disabled={!canStartFilling}>
                <i className="bi bi-play-circle-fill me-2" /> Iniciar Llenado
              </button>
              {!canStartFilling && (
                <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                  Complete cantidad y cliente en "Entradas de Cilindros" abajo para poder iniciar.
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-center mb-3">
                <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {pausedAt ? 'Llenado Pausado' : 'Llenado en Curso'}
                </div>
                <div style={{ fontSize: '3.5rem', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1, color: pausedAt ? '#f0ad4e' : undefined }}>
                  {formatElapsedMs(computeElapsedMs(fillingStartedAt, pausedAt, pausedDurationMs, now))}
                </div>
              </div>
              <div className="d-flex" style={{ gap: 8 }}>
                {pausedAt ? (
                  <button className="btn btn-success flex-grow-1" onClick={handleResumeFilling}>
                    <i className="bi bi-play-fill me-1" /> Reanudar
                  </button>
                ) : (
                  <button className="btn btn-outline-secondary flex-grow-1" onClick={handlePauseFilling}>
                    <i className="bi bi-pause-fill me-1" /> Pausar
                  </button>
                )}
                <button className="btn btn-outline-secondary flex-grow-1" onClick={handleHoldFilling}>
                  Retener
                </button>
                <button className="btn btn-danger flex-grow-1" onClick={handleFinishFilling}>
                  <i className="bi bi-stop-fill me-1" /> Finalizar Llenado
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}

    <DryerStatusPanel openSetupSignal={dryerEditSignal} reloadSignal={dryerReloadSignal} productionOn={isOperacionRunning} />

    {/* 🧪 Cylinder Entry Forms */}
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-header fw-bold bg-light">Entradas de Cilindros</div>
      <div className="card-body">
        {forms.map((form, index) => (
          <div key={index} className="row g-3 mb-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label">Cantidad</label>
              <div className="input-group">
                <button className="btn btn-outline-secondary" onClick={() => handleAmountChange(index, -1)} disabled={!activeGas}>-</button>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={form.amount}
                  onChange={(e) => handleAmountInput(index, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="form-control text-center fw-bold"
                  disabled={!activeGas}
                />
                <button className="btn btn-outline-secondary" onClick={() => handleAmountChange(index, 1)} disabled={!activeGas}>+</button>
              </div>
              {typeForClient(form.client) !== 'CC' && (
                <small className="text-muted">
                  Máx disponible: {pendingToFillByClient[form.client] || 0}
                </small>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Cliente</label>
              <input
                type="text"
                className="form-control"
                value={form.client}
                onChange={(e) => handleClientSearch(index, e.target.value)}
                onFocus={() => setShowDropdownIndex(index)}
              />
              {showDropdownIndex === index && (
                <div className="dropdown-menu show w-100 mt-1 shadow">
                  {!form.client.trim() && recentClients.filter(c => eligibleClients.includes(c)).length > 0 && (
                    <>
                      <div className="dropdown-header" style={{ fontSize: '0.75rem' }}>Recientes</div>
                      {recentClients.filter(c => eligibleClients.includes(c)).map((c) => (
                        <button key={`recent-${c}`} className="dropdown-item" onClick={() => selectClient(index, c)}>{c}</button>
                      ))}
                      <div className="dropdown-divider" />
                    </>
                  )}
                  {eligibleClients.filter(c => c.toLowerCase().includes(form.client.toLowerCase())).map((c, i) => (
                    <button key={i} className="dropdown-item" onClick={() => selectClient(index, c)}>
                      {c === COMPANY_CLIENT ? c : `${c} — ${pendingToFillByClient[c]} tanques pendientes`}
                    </button>
                  ))}
                </div>
              )}
              {pedidosForClient(form.client).length > 0 && (
                <div className="mt-2">
                  <label className="form-label">¿Para qué pedido? <small className="text-muted">(opcional)</small></label>
                  <select
                    className="form-select"
                    value={form.entryId || ''}
                    onChange={(e) => selectPedido(index, e.target.value)}
                  >
                    <option value="">Sin pedido específico</option>
                    {pedidosForClient(form.client).map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.ventasPedidoNumber || p._id}{p.ventasPedidoDate ? ` — ${formatDay(p.ventasPedidoDate)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {index > 0 && (
              <div className="col-md-1 text-end">
                <button className="btn btn-outline-danger" onClick={() => deleteForm(index)}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            )}
          </div>
        ))}

        <div className="d-flex justify-content-center mt-4" style={{ gap: '12px' }}>
          <button onClick={addForm} className="btn btn-outline-primary">
            <i className="bi bi-plus-circle me-2" /> Agregar Línea
          </button>
          <button className="btn btn-outline-danger" onClick={() => setShowDamageModal(true)}>
            <i className="bi bi-exclamation-triangle me-2" /> Reportar Tanque Averiado
          </button>
        </div>
      </div>
    </div>

    {/* ⏰ Time Selection */}
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-header fw-bold bg-light">Tiempos de Producción</div>
      <div className="card-body row g-4">
        <div className="col-md-6">
          <label>Hora de Inicio</label>
          <input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="form-control" />
          <button className="btn btn-sm btn-warning mt-2 w-100" onClick={() => setCurrentTime(setStartTime)}>Ahora</button>
        </div>
        <div className="col-md-6">
          <label>Hora de Fin</label>
          <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="form-control" />
          <button className="btn btn-sm btn-warning mt-2 w-100" onClick={() => setCurrentTime(setEndTime)}>Ahora</button>
        </div>
      </div>
    </div>

    {/* ✅ Save Button */}
    <div className="text-center mb-4">
      <button onClick={saveForm} className="btn btn-success btn-lg px-5">
        <i className="bi bi-save me-2" /> Guardar
      </button>
      {errorMessage && <p className="text-danger mt-3 fw-bold">{errorMessage}</p>}
    </div>
{Array.isArray(entries) && entries.length > 0 && (
  <div className="row mb-4">
    {/* Time per filling chart */}
    <div className="col-md-6">
      <div className="card shadow-sm p-3">
        <h6 className="fw-bold text-center">⏱ Tiempo por Llenado</h6>
        <Line
          data={{
            labels: entries.map(e =>
              formatDateTime(e.startTime, 'Sin Hora')
            ),
            datasets: [
              {
                label: 'Duración (min)',
                data: entries.map(e =>
                  e.startTime && e.endTime
                    ? (new Date(e.endTime) - new Date(e.startTime)) / 60000
                    : null
                ),
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.3,
                fill: false,
              },
            ],
          }}
          options={{
            responsive: true,
            scales: {
              y: { title: { display: true, text: 'Minutos' }, beginAtZero: true },
              x: { title: { display: true, text: 'Hora de Inicio' } },
            },
          }}
        />
      </div>
    </div>

    {/* Cylinders per filler per day */}
    <div className="col-md-6">
      <div className="card shadow-sm p-3">
        <h6 className="fw-bold text-center">📈 Cilindros por Día por Operador</h6>
        <Line
          data={{
            labels: getFillingLabels(entries).map(dayLabel),
            datasets: getFillerDatasets(entries),
          }}
          options={{
            responsive: true,
            plugins: { legend: { position: 'bottom' } },
            scales: {
              y: { title: { display: true, text: 'Cilindros' }, beginAtZero: true },
              x: { title: { display: true, text: 'Fecha' } },
            },
          }}
        />
      </div>
    </div>
  </div>
)}

    {/* 📋 Production Table */}
    <div className="card shadow-sm border-0 mb-5">
      <div className="card-header bg-light fw-bold">Historial de Producción</div>
      <div className="card-body">
        <ProductionTable userName="Filler" entries={entries} setEntries={setEntries} />
      </div>
    </div>

    <Modal
  show={showFillingOrderModal}
  onHide={() => setShowFillingOrderModal(false)}
  size="lg"
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Orden de Llenado</Modal.Title>
  </Modal.Header>
  <Modal.Body>
{fillingOrderData.length > 0 && (
  <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
    {fillingOrderData.length} cliente{fillingOrderData.length === 1 ? '' : 's'} pendiente{fillingOrderData.length === 1 ? '' : 's'} ·{' '}
    {fillingOrderData.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0)} cilindros en total
  </div>
)}
<Table className="table-sm align-middle" bordered hover>
  <thead className="table-light sticky-top">
    <tr>
      <th style={{ width: '48px' }}>#</th>
      <th>Cliente</th>
      <th>Cantidad a Llenar</th>
      <th style={{ width: '140px' }}></th>
    </tr>
  </thead>
  <tbody>
    {fillingOrderData.length > 0 ? (
      fillingOrderData.map((entry, index) => (
        <tr key={entry._id}>
          <td className="text-muted">{index + 1}</td>
          <td>{entry.client}</td>
          <td>{entry.quantity}</td>
          <td className="text-center">
            <Button
              variant="success"
              size="sm"
              onClick={() => markCompleted(entry._id)}
              disabled={savingRowId === entry._id}
            >
              {savingRowId === entry._id ? '⏳' : <><i className="bi bi-check-lg me-1" />Completado</>}
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="4" className="text-center text-muted">
          No hay clientes pendientes de llenado.
        </td>
      </tr>
    )}
  </tbody>
</Table>


  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowFillingOrderModal(false)}>
      Cerrar
    </Button>
  </Modal.Footer>
</Modal>

{/* 🔔 Blocks until acknowledged — the admin changed the filling order */}
<Modal show={showOrderChangedAlert} backdrop="static" keyboard={false} centered>
  <Modal.Header>
    <Modal.Title>🔔 El Orden de Llenado Cambió</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    Un administrador actualizó el orden de llenado. Revíselo antes de continuar.
  </Modal.Body>
  <Modal.Footer>
    <Button variant="primary" onClick={acknowledgeOrderChange}>
      Entendido, ver orden
    </Button>
  </Modal.Footer>
</Modal>

      <Modal show={showDamageSuccess} onHide={() => setShowDamageSuccess(false)} centered>
  <Modal.Header closeButton>
    <Modal.Title>Reporte Exitoso</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <div className="text-success fw-bold text-center">
      🛠️ El reporte de tanque averiado fue guardado correctamente.
    </div>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="success" onClick={() => setShowDamageSuccess(false)}>
      Cerrar
    </Button>
  </Modal.Footer>
</Modal>


      <Modal show={!!errorMessage} onHide={() => setErrorMessage('')} centered>
        <Modal.Header closeButton><Modal.Title>Error</Modal.Title></Modal.Header>
        <Modal.Body className="text-danger">{errorMessage}</Modal.Body>
        <Modal.Footer><Button variant="danger" onClick={() => setErrorMessage('')}>Cerrar</Button></Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Éxito</Modal.Title></Modal.Header>
        <Modal.Body>Llenado guardado exitosamente.</Modal.Body>
        <Modal.Footer><Button variant="success" onClick={() => setShowSuccessModal(false)}>OK</Button></Modal.Footer>
      </Modal>

      <Modal show={!!showStopModal} onHide={() => setShowStopModal(null)} centered>
        <Modal.Header closeButton><Modal.Title>Detener {showStopModal && STREAM_STOP_LABEL[showStopModal]}</Modal.Title></Modal.Header>
        <Modal.Body>
          <label>Motivo</label>
          <select className="form-control mb-3" value={stopReason} onChange={(e) => setStopReason(e.target.value)}>
            <option value="">Seleccione un motivo</option>
            <option value="Mantenimiento">Mantenimiento</option>
            <option value="Apagon Electrico">Apagón</option>
            <option value="Falta de Cilindros">Falta de Cilindros</option>
            <option value="Otro">Otro</option>
          </select>
          <label>Notas</label>
          <textarea className="form-control" rows="3" value={stopNotes} onChange={(e) => setStopNotes(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStopModal(null)}>Cancelar</Button>
          <Button variant="danger" onClick={handleStopProduction}>Detener</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!dryerConfirmState} onHide={() => setDryerConfirmState(null)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Estado de Secadores</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Antes de iniciar producción, confirme que las posiciones de las válvulas coinciden con este estado:</p>
          {dryerConfirmState && (
            <div style={{ fontFamily: 'monospace', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>
                Secador {dryerConfirmState.active.dryer}: {dryerStageLabel('en_servicio')} —{' '}
                {formatElapsedMs(computeElapsedMs(
                  dryerConfirmState.active.since,
                  dryerConfirmState.active.pausedAt,
                  dryerConfirmState.active.pausedDurationMs,
                  Date.now(),
                ))}
              </div>
              <div>
                Secador {dryerConfirmState.regenerating.dryer}: {dryerStageLabel(dryerConfirmState.regenerating.stage)} —{' '}
                {formatElapsedMs(computeElapsedMs(
                  dryerConfirmState.regenerating.since,
                  dryerConfirmState.regenerating.pausedAt,
                  dryerConfirmState.regenerating.pausedDurationMs,
                  Date.now(),
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDryerConfirmState(null)}>Cancelar</Button>
          <Button variant="outline-primary" onClick={editDryersInstead}>Editar Estado de Secadores</Button>
          <Button variant="success" onClick={confirmDryersAndStartProduction}>✔ Confirmar e Iniciar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={heldModalOpen} onHide={() => setHeldModalOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Llenados en Espera</Modal.Title></Modal.Header>
        <Modal.Body>
          {fillingStartedAt && (
            <div className="alert alert-warning py-2">Termina o retén el llenado actual primero.</div>
          )}
          {heldItems.length === 0 ? (
            <div className="text-muted">No hay llenados en espera.</div>
          ) : (
            <div className="d-flex flex-column" style={{ gap: 12 }}>
              {heldItems.map((item) => {
                const elapsed = computeElapsedMs(item.startedAt, item.pausedAt, item.pausedDurationMs, Date.now());
                const clients = [...new Set((item.forms || []).map((f) => f.client).filter(Boolean))].join(', ');
                return (
                  <div key={item.id} className="border rounded p-3">
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>{clients || '—'}</div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, fontFamily: 'monospace' }}>{formatElapsedMs(elapsed)}</div>
                    <button
                      className="btn btn-success btn-sm mt-2"
                      disabled={!!fillingStartedAt}
                      onClick={() => handleResumeHeld(item.id)}
                    >
                      <i className="bi bi-play-fill me-1" /> Reanudar
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setHeldModalOpen(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={heldCapModalOpen} onHide={() => setHeldCapModalOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Límite alcanzado</Modal.Title></Modal.Header>
        <Modal.Body>Puedes tener máximo 2 llenados en espera. Finaliza o reanuda uno primero.</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setHeldCapModalOpen(false)}>Entendido</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!longFillingWarning} onHide={() => setLongFillingWarning(null)} centered>
        <Modal.Header closeButton><Modal.Title>Llenado largo</Modal.Title></Modal.Header>
        <Modal.Body>
          {longFillingWarning && (
            <>
              <p>
                Está registrando un llenado de{' '}
                <strong>
                  {Math.round(((longFillingWarning.endTimeDate - longFillingWarning.startTimeDate) - (longFillingWarning.pausedMs || 0)) / 60000)} minutos
                </strong>.
              </p>
              <p>
                Inició a las {longFillingWarning.startTimeDate.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
                {' '}y finalizó a las {longFillingWarning.endTimeDate.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}.
              </p>
              <p className="mb-0">¿Está seguro?</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLongFillingWarning(null)} disabled={confirmingLong}>Cancelar</Button>
          <Button variant="warning" onClick={confirmLongFilling} disabled={confirmingLong}>
            {confirmingLong ? 'Guardando...' : 'Sí, guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDamageModal} onHide={() => setShowDamageModal(false)} centered>
  <Modal.Header closeButton>
    <Modal.Title>Reporte de Tanque Averiado</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <label>Cantidad</label>
    <input type="number" min="1" className="form-control mb-3" value={damageQty} onChange={(e) => setDamageQty(e.target.value)} />

    <label>Propietario</label>
    <select className="form-select mb-3" value={damageOwner} onChange={(e) => setDamageOwner(e.target.value)}>
      <option value="">Seleccione</option>
      <option value="Cilindro de la Compañía">Cilindro de la Compañía</option>
      {clientList.map((c, i) => (
        <option key={i} value={c}>{c}</option>
      ))}
    </select>

    <label>Notas</label>
    <textarea className="form-control" rows="3" value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)} />
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowDamageModal(false)}>Cancelar</Button>
    <Button variant="danger" onClick={handleSaveDamageReport}>Guardar Reporte</Button>
  </Modal.Footer>
</Modal>

  </div>
);


};



export default CylindersFilling;
